import { useCallback, useRef } from "react";

/**
 * Used to animate the height transition of an element from being
 * collapsed to being fully expanded, and vice-versa. Based on
 * [this css-tricks article](https://css-tricks.com/using-css-transitions-auto-dimensions/#aa-technique-3-javascript)
 * ([Codepen](https://codepen.io/brundolf/pen/dvoGyw?editors=0100))
 *
 * ---
 * @returns The `RefObject` of the collapsible element, as well as a callback for collapsing and expanding the collapsible
 */

export function useCollapsibleAnimation(
  isOpen: boolean,
  setIsOpen: (value: boolean) => void
) {
  let elementRef = useRef<HTMLDivElement | null>(null);

  const elementRefCallback = useCallback((marker: HTMLDivElement | null) => {
    elementRef.current = marker;
    if (isOpen) {
      expandSection();
    } else {
      collapseSection();
    }
  }, []);

  function collapseSection() {
    if (elementRef.current) {
      let collapsibleElement = elementRef.current;
      let sectionHeight = collapsibleElement.scrollHeight;

      let elementTransition = collapsibleElement.style.transition;
      collapsibleElement.style.transition = "";

      requestAnimationFrame(function () {
        collapsibleElement.style.height = sectionHeight + "px";
        collapsibleElement.style.transition = elementTransition;

        requestAnimationFrame(function () {
          collapsibleElement.style.height = 0 + "px";
        });
      });

      setIsOpen(false);
    }
  }

  function expandSection() {
    if (elementRef.current) {
      let collapsibleElement = elementRef.current;
      let sectionHeight = collapsibleElement.scrollHeight;

      collapsibleElement.style.height = sectionHeight + "px";

      collapsibleElement.addEventListener(
        "transitionend",
        function onTransitionEnd() {
          collapsibleElement.removeEventListener(
            "transitionend",
            onTransitionEnd
          );

          collapsibleElement.style.height = "auto";
        }
      );

      setIsOpen(true);
    }
  }

  function toggleCollapsible() {
    if (elementRef.current) {
      if (isOpen) {
        collapseSection();
      } else {
        expandSection();
        setIsOpen(true);
      }
    }
  }

  return {
    /** The `RefObject` of the collapsible element */
    elementRef,
    /** Used to assign the collapsible HTML/Jsx 
    element to the `RefObject` on initialization */
    elementRefCallback,
    /** Used to toggle the state of the collapsible
    element between being collapsed and expanded */
    toggleCollapsible,
  };
}
